<script lang="ts">
  // Svelte / Kit

  // UI components
  import ControlButtonsContainer from '$lib/components/ControlButtonsContainer.svelte';
  import * as AlertDialog from '$lib/components/ui/alert-dialog';
  import { Button } from '$lib/components/ui/button';
  import * as Table from '$lib/components/ui/table';
  import { CircleX, Ellipsis } from '@lucide/svelte';

  // RoastLog
  import { roastingEvents } from '$lib/RoastingEvents.svelte';
  import EventName from '$lib/components/roastingEvents/EventName.svelte';
  import ValueSelectors from '$lib/components/roastingEvents/ValueSelectors.svelte';
  import * as DropdownMenu from '$lib/components/ui/dropdown-menu';
  import type { RoastingEvent } from '$lib/types/RoastLog';
  import logger from '$lib/logger';

  let deleteItemDialogOpen = $state(false);
  let indexToDelete: number | null = null;

  const handleAddRoastingEvent = async (): Promise<void> => {
    const n: RoastingEvent = {
      name: '',
      type: 'equipment',
      scale: { min: '0.0', max: '10.0', step: '0.5' }
    };
    roastingEvents.append(n);
  };

  const sendUpdate = async (i: number) => {
    const event = roastingEvents.at(i);
    if (roastingEvents.isNewEvent(event) && event.name) {
      logger.info('Sending new roasting event', { event });
      roastingEvents.createNewEventAt(i, event);
    } else if (event.name) {
      logger.info('Updating existing roasting event', { event });
      roastingEvents.updateEventAt(i, event);
    }
  };

  const handleDeleteRoastingEvent = async (): Promise<void> => {
    if (indexToDelete !== null) {
      const deletedEvent = roastingEvents.deleteItemByIndex(indexToDelete);
      logger.info('Deleting roasting event', { deletedEvent });
      indexToDelete = null;
    }
    deleteItemDialogOpen = false;
  };
</script>

<ControlButtonsContainer>
  <Button onclick={handleAddRoastingEvent}>Add Event</Button>
</ControlButtonsContainer>

<!-- Delete a row dialog -->
<AlertDialog.Root bind:open={deleteItemDialogOpen}>
  <AlertDialog.Content>
    <AlertDialog.Header>
      <AlertDialog.Title>Delete item?</AlertDialog.Title>
      <AlertDialog.Description>
        Are you sure you want to delete this roasting event?
      </AlertDialog.Description>
    </AlertDialog.Header>
    <AlertDialog.Footer>
      <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
      <AlertDialog.Action onclick={handleDeleteRoastingEvent}>Delete</AlertDialog.Action>
    </AlertDialog.Footer>
  </AlertDialog.Content>
</AlertDialog.Root>

<div class="bg-card flex h-[calc(100vh-138px)] flex-col justify-normal rounded-lg p-5 pt-2">
  <Table.Root class="mt-2 w-full">
    <Table.Header>
      <Table.Head class="w-5 px-4 py-3" scope="col">Color</Table.Head>
      <Table.Head class="px-4 py-3" scope="col">Description</Table.Head>
      <Table.Head class="w-72 px-4 py-3" scope="col">Value selectors</Table.Head>
      <Table.Head class="w-5 px-4 py-3" scope="col">Actions</Table.Head>
    </Table.Header>

    <Table.Body>
      {#each roastingEvents.items as roastingEvent, i}
        <Table.Row>
          <Table.Cell class="w-5">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" fill={roastingEvent.color} />
            </svg>
          </Table.Cell>
          <Table.Cell>
            <EventName bind:name={roastingEvent.name} counter={i} {sendUpdate} />
          </Table.Cell>
          <Table.Cell>
            <ValueSelectors bind:eventScale={roastingEvent.scale} counter={i} {sendUpdate} />
          </Table.Cell>
          <Table.Cell>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger>
                <Ellipsis />
              </DropdownMenu.Trigger>
              <DropdownMenu.Content>
                <DropdownMenu.Group>
                  <DropdownMenu.Item
                    onclick={() => {
                      indexToDelete = i;
                      deleteItemDialogOpen = true;
                    }}
                  >
                    <CircleX class="mr-2 h-4 w-4" /> Delete
                  </DropdownMenu.Item>
                </DropdownMenu.Group>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </Table.Cell>
        </Table.Row>
      {/each}
    </Table.Body>
  </Table.Root>
</div>
